import { useTranslation } from "react-i18next";
import { Avatar, Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import casesService from "api/casesService";
import tagsService from "api/tagsService";
import BinIcon from "assets/icons/delete_24.svg?react";
import CloseIcon from "assets/icons/close.svg?react";
import { IDropdownObject } from "components/Dropdown/typings";
import SearchEntities from "components/SearchEntities";
import CasesContext from "contexts/CasesContext";
import { CaseParticipantPrivileges } from "dto/Cases/CaseParticipantPrivileges";
import { ICaseParticipant } from "dto/Cases/ICaseParticipant";
import { IEntity } from "dto/IEntity";
import { ITag } from "dto/ITag";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { convertToLetters } from "utils/convertToLetters";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import Button from "components/Button";
import { useOnClickOutside } from "hooks/useClickOutside";
import { ENotificationType, notification } from "utils/notification";
import ConfirmCloseModal from "components/ConfirmCloseModal";
import TeamIcon from "assets/icons/team.svg?react";
import CourtIcon from "assets/icons/court.svg?react";
import HollowButton from "components/HollowButton";
import LabelIcon from "assets/icons/label-new.svg?react";
import DotsIcon from "assets/icons/three-dots.svg?react";
import { CaseParticipantStatus } from "dto/Cases/CaseParticipantStatus";
import { CaseParticipantGroup } from "dto/Cases/CaseParticipantGroup";
import { Option } from "components/OptionsCard/typings";
import ArrowsIcon from "assets/icons/arrows.svg?react";
import { default as Dropdown2 } from "components/OptionsCard/Dropdown";
import { randomString } from "utils/randomString";
import { EEntityType } from "dto/EEntityType";
import { isNumber } from "utils/isNumber";
import classNames from "classnames";
import EmailIcon from "assets/icons/aemail.svg?react";
import LetterIcon from "assets/icons/new_parcel.svg?react";
import Spinner from "components/Spinner";
import AddButton from "components/AddButton";
import { logger } from "utils/logger";
import InfoIcon from "assets/icons/info_icon.svg?react";
import CaseParticipantEditorModal from "components/CaseParticipantEditorModal";
import InputInline from "components/InputInline";
import DropdownMui from "components/DropdownMui";
import NotFoundScreen from "components/NotFoundScreen";
import ModalLayout, { DockPosition } from "components/ModalLayout";

const CaseParticipantConflictResolver = ({
  participant,
  onResolveConflict,
  toggle,
}: {
  participant: ICaseParticipant;
  onResolveConflict: (participant: ICaseParticipant) => void;
  toggle: (state: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { data, isLoading } =
    casesService.useSearchCaseParticipantDuplicatesFromCourtPortal({
      id: participant.CourtPortalId,
    });
  const ref = useRef(null);

  const [isCourtRecordSelected, setIsCourtPortalSelected] =
    useState<boolean>(false);
  const [selected, setSelected] = useState<ICaseParticipant>(null);

  const handleResolve = () => {
    if (isCourtRecordSelected) {
      onResolveConflict(participant);
    } else {
      selected.CourtPortalRole = participant.CourtPortalRole;
      onResolveConflict(selected);
    }
    toggle(false);
  };

  return (
    <div ref={ref} className={classNames(styles.wrapper, styles.small)}>
      <div className={styles.hdr}>
        <div>{t("participants.similarEntities")}</div>
        <CloseIcon
          onClick={() => {
            toggle(false);
          }}
          className={styles.closeIcon}
        />
      </div>

      <div className={styles.list}>
        {isLoading && <Spinner></Spinner>}
        {data?.data &&
          data?.data.map((r) => {
            return (
              <div
                key={r.Id}
                className={classNames(
                  styles.listItem,
                  selected?.Id === r.Id ? styles.selected : null
                )}
                onClick={() => {
                  setIsCourtPortalSelected(false);
                  setSelected(r);
                }}
              >
                <div className={styles.name}>{r.Label}</div>

                {
                  <div className={styles.email}>
                    <div className={styles.icon}>
                      <LetterIcon></LetterIcon>
                    </div>
                    {r.Address ?? t("common.noAddressData")}
                  </div>
                }
                {r.Email && (
                  <div className={styles.email}>
                    <div className={styles.icon}>
                      <EmailIcon></EmailIcon>
                    </div>
                    {r.Email}
                  </div>
                )}
              </div>
            );
          })}
      </div>
      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={isCourtRecordSelected}
              onChange={(e) => {
                setIsCourtPortalSelected(e.target.checked);
                if (e.target.checked) {
                  setSelected(null);
                }
              }}
            />
          }
          label={t("participants.createNewFromPI")}
        ></FormControlLabel>
      </div>

      <div className={styles.btnWrapper}>
        <Button
          text={isCourtRecordSelected ? t("common.create") : t("common.link")}
          onClick={() => handleResolve()}
          disabled={selected == null && !isCourtRecordSelected}
        />
      </div>
    </div>
  );
};

const EntityRow = ({
  participant,
  group,
  onRemove,
  onResolveConflict,
  afterEdit,
  setIsModified,
  allTags,
  conflictsOnly = false,
  showInvitationsColumn = true,
}: {
  participant: ICaseParticipant;
  group?: CaseParticipantGroup;
  onRemove?: (participant: ICaseParticipant) => void;
  onResolveConflict: (
    participant: ICaseParticipant,
    replaceWith?: ICaseParticipant
  ) => void;
  afterEdit: (participant: ICaseParticipant) => void;
  setIsModified: (value: boolean) => void;
  allTags: ITag[];
  canEdit: boolean;
  conflictsOnly: boolean;
  showInvitationsColumn: boolean;
}) => {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [options, setOptions] = useState<Option[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [name] = useState<string>(participant.Label);
  const [showResolveConflict, setShowResolveConflict] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [editInline, setEditInline] = useState(false);
  const refWrapper = useRef();
  const refEditInput = useRef();


  const shareTypes: IDropdownObject[] = [
    {
      value: 0,
      name: t("shareTypes.none"),
    },
    {
      value: 1,
      name: t("shareTypes.read"),
    },
    {
      value: 2,
      name: t("shareTypes.edit"),
    },
  ];

  const Rlist = allTags
    ? allTags
        ?.filter((tag: ITag) => tag.Type === 3)
        ?.map((tag: ITag) => {
          return {
            name: tag.Name,
            value: tag.Id,
            AdditionalInfo:
              tag.Global && tag.Name === "Klient" ? (
                <div>
                  <Tooltip title={t("participants.clientRoleTooltip")}>
                    <div>
                      <InfoIcon></InfoIcon>
                    </div>
                  </Tooltip>
                </div>
              ) : null,
          };
        })
    : [];

  useOnClickOutside(refEditInput, () => {
    if (isEditing) {
      commitName(name);
    }
  });

  const setRole = (participant: ICaseParticipant, role?: IDropdownObject) => {
    participant.RoleId = role ? (role.value as string) : null;
    setIsModified(true);
  };

  const setShare = (participant: ICaseParticipant, share?: IDropdownObject) => {
    participant.Privileges = share
      ? (share.value as number)
      : CaseParticipantPrivileges.None;
    setIsModified(true);
  };

  const handleEditInline = () => {
    setEditInline(true);
  };

  useEffect(() => {
    let opt =
      group != null
        ? [
            {
              name: t("common.delete"),
              Icon: BinIcon,
              callback: () => {
                onRemove(participant);
              },
              additional: false,
              isEnabled: true,
              canMultiple: true,
            },
            {
              name: t("participants.editEntityName"),
              Icon: LabelIcon,
              callback: () => {
                handleEditInline();
              },
              additional: true,
              isEnabled: true,
              canMultiple: true,
            },
            {
              name: t("participants.editEntityData"),
              Icon: LabelIcon,
              callback: () => {
                setShowInfo(true);
              },
              additional: true,
              isEnabled: true,
              canMultiple: true,
            },
          ]
        : [];

    if (participant.HasConflict) {
      opt = [
        {
          name: t("participants.linkEntityData"),
          Icon: ArrowsIcon,
          callback: () => {
            setShowResolveConflict(true);
          },
          additional: false,
          isEnabled: true,
          canMultiple: true,
        },
        ...opt,
      ];
    }

    // if (group == null || group === CaseParticipantGroup.Normal) {
    //   opt.push({
    //     name: 'Przenieś do "Dw"',
    //     Icon: MoveIcon,
    //     callback: () => {
    //       onMove(participant, true);
    //     },
    //     additional: false,
    //     isEnabled: true,
    //     canMultiple: true,
    //   });
    // }

    setOptions(opt);
  }, [participant]);

  const handleResolveConflict = (p: ICaseParticipant) => {
    // rozwiązujemy konflikt używając piertownego rekordu
    if (
      p.Id === participant.Id &&
      p.CourtPortalId === participant.CourtPortalId
    ) {
      onResolveConflict(participant, null);
    }
    // rozwiązujemy konflikt używając wskazanego rekordu
    else {
      onResolveConflict(participant, p);
    }
  };

  const commitName = (val?: string) => {
    console.log("Setting name", val ?? name);
    participant.Name = val ?? name;
    participant.Label = val ?? name;
    afterEdit(participant);
    setIsEditing(false);
    setEditInline(false);
  };

  const handleUpdateParticipant = (p: ICaseParticipant) => {
    afterEdit(p);
  };



  if (editInline) {
    return (
      <div className={styles.entityBox}>
        <div className={styles.entity}>
          <Avatar className={styles.avatar}>
            {convertToLetters(participant?.Label ?? "")}
          </Avatar>
          <div className={styles.grow}>
            <div className={styles.label}>
              <InputInline
                value={participant.Label}
                onCancel={() => setEditInline(false)}
                onChange={commitName}
              ></InputInline>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.entityBox}>
      <div className={styles.entity}>
        <Avatar className={styles.avatar}>
          {convertToLetters(participant?.Label ?? "")}
        </Avatar>
        <div className={styles.labelWrapper}>
          <div className={styles.label}>{participant?.Label ?? ""} </div>
          <div className={styles.icon}>
            <Tooltip title={t("participants.entityData")}>
              <div>
                <InfoIcon onClick={() => setShowInfo(!showInfo)}></InfoIcon>
              </div>
            </Tooltip>
          </div>
          {participant.HasConflict && (
            <div className={styles.errorBlock}>
              {t("participants.similarEntityDetected")}
            </div>
          )}
        </div>
      </div>
      <div className={styles.boxes}>
        {!conflictsOnly && (
          <div className={styles.box}>
            <DropdownMui
              variant="standard"
              size="small"
              hideBorder={true}
              defaultValue={participant.RoleId ?? ""}
              className={styles.parcelDropdown}
              onChange={(r) => setRole(participant, r)}
              list={Rlist}
              placeholder={participant.CourtPortalRole ?? t("common.none")}
              allowNull={true}
            />
          </div>
        )}
        {/* <div className={styles.box}>
                    <Dropdown
                      defaultValue={participant.DefaultParcelType}
                      className={styles.parcelDropdown}
                      onChange={(r) => setParcel(participant, r)}
                      list={parcelTypes}
                      placeholder="Brak"
                      noBackground
                      allowNull
                      disableChange={!canEdit}
                    />
                  </div> */}
        {!conflictsOnly && showInvitationsColumn && (
          <div className={styles.box}>
            {(participant.Status === CaseParticipantStatus.Invitation ||
              participant.Status ===
                CaseParticipantStatus.WaitingToConfirm) && (
              <div className={styles.invitation}>{t("participants.invited")}</div>
            )}
            {participant.Status !== CaseParticipantStatus.Invitation && (
              <DropdownMui
                variant="standard"
                size="small"
                hideBorder={true}
                defaultValue={participant.Privileges ?? 0}
                className={styles.parcelDropdown}
                onChange={(r) => setShare(participant, r)}
                list={shareTypes}
              />
            )}
          </div>
        )}
        <div className={styles.more} ref={refWrapper}>
          <Tooltip title={t("common.more")}>
            <div>
              <DotsIcon
                onClick={() => {
                  setShowDropdown(!showDropdown);
                }}
                className={classNames(
                  styles.icon,
                  participant.HasConflict ? styles.conflict : null
                )}
              />
            </div>
          </Tooltip>
          <Dropdown2
            toggle={() => setShowDropdown(false)}
            options={options}
            refWrapper={refWrapper}
            rightSide={false}
            scrollWrapper={undefined}
            showOptions={showDropdown}
            selectedElements={[]}
          />
          {showResolveConflict && (
            <div className={styles.dropdown2}>
              <CaseParticipantConflictResolver
                toggle={() => setShowResolveConflict(false)}
                participant={participant}
                onResolveConflict={handleResolveConflict}
              ></CaseParticipantConflictResolver>
            </div>
          )}

          {showInfo && (
            <ModalLayout
              dock={DockPosition.BottomRight}
              toggle={() => setShowInfo(false)}
              refWrapper={refWrapper}
              className={styles.innerModal}
              disableMove={true}
            >
              <CaseParticipantEditorModal
                toggle={() => setShowInfo(false)}
                source={participant}
                update={handleUpdateParticipant}
              ></CaseParticipantEditorModal>
            </ModalLayout>
          )}
        </div>
      </div>
    </div>
  );
};

const CaseParticipantsModal: FC<Props> = ({
  toggle,
  Case,
  sourceParticipants,
  onApply,
  conflictsOnly = false,
  showInvitationsColumn = true,
}) => {
  const { t } = useTranslation();
  const { data: fullCase, isLoading } = casesService.useGetCase(
    Case?.Id,
    null,
    Case?.Id !== null
  );

  const { mutate: createCase } = casesService.useCreateCase();
  const { cases, setCases } = useContext(CasesContext);

  const [isModified, setIsModified] = useState<boolean>(false);
  const [addNew, setAddNew] = useState<boolean>(false);
  const [participants, setParticipants] = useState<ICaseParticipant[]>([]);
  const [showModal, setShowModal] = useState(false);

  const handleAdd = () => {
    setAddNew(true);
  };

  const handleCommitAdd = (v: string) => {
    const payload = {
      Id: randomString(),
      Type: EEntityType.New,
      Email: null,
      EDeliveryAddress: null,
      Address: null,
      Label: v,
      RoleId: null,
      ParcelTypes: 0,
      Privileges: 0,
      DefaultParcelType: null,
      IsMain: false,
    };

    setParticipants([...participants, payload]);

    setAddNew(false);
    setIsModified(true);
  };

  const handleNewEntity = (entity: IEntity, cc: boolean) => {
    const payload = {
      Id: randomString(),
      UserId:
        entity.Type === EEntityType.EDelivery ||
        entity.Type === EEntityType.NotaGuest
          ? entity.Id
          : undefined,
      ParticipantId:
        entity.Type === EEntityType.NotaParticipant ? entity.Id : undefined,
      Type: entity.Type,
      Email: entity.Email,
      EDeliveryAddress: entity.EDeliveryAddress,
      Address: entity.Address,
      Label: entity.Label ?? entity.Name,
      RoleId: entity.RoleId ?? null,
      ParcelTypes: entity.ParcelTypes,
      DefaultParcelType: null,
      IsMain: false,
      Group: cc ? CaseParticipantGroup.Cc : CaseParticipantGroup.Normal,
    };

    setParticipants([...participants, payload]);

    setAddNew(false);
    setIsModified(true);
  };

  const handleRemove = (participant: ICaseParticipant) => {
    const index = participants.indexOf(participant, 0);

    if (index > -1) {
      const arr = participants;
      arr.splice(index, 1);
      setParticipants([...arr]);

      setIsModified(true);
    }
  };

  // const handleMove = (item: ICaseParticipant, cc: boolean) => {
  //   var normalSource = part.find((x) => x.Id === item.Id);

  //   if (normalSource) {
  //     item.Group = CaseParticipantGroup.Cc;
  //     setNormalParticipants((old) => old.filter((x) => x.Id !== item.Id));
  //   }
  // };

  const handleResolveConflict = (
    item: ICaseParticipant,
    replaceWith?: ICaseParticipant
  ) => {
    logger.log(
      "Resolving conflict for entity",
      item,
      "replacing with",
      replaceWith ?? "none"
    );
    const copy = [...participants];
    const part = copy.find(
      (x) => x.Id === item.Id && x.CourtPortalId === item.CourtPortalId
    );

    if (part) {
      if (!replaceWith) {
        part.HasConflict = false;
      } else {
        const index = copy.indexOf(part);

        copy[index] = replaceWith;
      }

      setParticipants(copy);
    }
  };

  const updateParticipantAfterEdit = (
    item: ICaseParticipant,
    newValue?: ICaseParticipant
  ) => {
    const copy = [...participants];
    const part = copy.find(
      (x) => x.Id === item.Id && x.CourtPortalId === item.CourtPortalId
    );

    if (part) {
      const index = copy.indexOf(part);
      copy[index] = newValue;
      setParticipants(copy);
    }
  };

  useEffect(() => {
    setParticipants(fullCase?.Participants ?? []);
    setIsModified(false);
  }, [fullCase]);

  useEffect(() => {
    if (sourceParticipants) {
      if (!conflictsOnly) {
        setParticipants(sourceParticipants);
      } else {
        setParticipants(sourceParticipants.filter((x) => x.HasConflict));
      }
    }
    setIsModified(false);
  }, [sourceParticipants]);

  const save = (force = false, close = true) => {
    if (onApply) {
      onApply(participants);
      toggle(false);
    }

    if (isModified || force) {
      participants.forEach((p) => {
        if (!isNumber(p.Id)) {
          p.Id = null;
        }
      });

      createCase(
        {
          Id: Case?.Id ?? null,
          Participants: participants,
          Signature: Case.Signature.replaceAll("<em>", "").replaceAll(
            "</em>",
            ""
          ),
          TagIds: Case.TagIds,
          Title: Case.Title.replaceAll("<em>", "").replaceAll("</em>", ""),
          StatusId: Case.StatusId as number,
        },
        {
          onError: (e) => {
            notification({
              text: e.response.data.errorCodes[0].message,
              title: t("errors.occurred"),
              type: ENotificationType.ERROR,
            });
          },
          onSuccess: (r) => {
            Case.Client = r.data.Client;
            Case.Court = r.data.Court;
            Case.Participants = r.data.Participants;

            const newCases = [...cases];

            if (Case) {
              const toUpdate = newCases.find((c) => c.Id === Case.Id);

              if (toUpdate) {
                toUpdate.Client = r.data.Client;
                toUpdate.Court = r.data.Court;

                setCases(newCases);
              }
            }

            setIsModified(false);
            if (close) {
              toggle(false);
            }
          },
        }
      );
    } else {
      toggle(false);
    }
  };

  const { data: tagsData, isLoading: isLoadingTags } = tagsService.useGetTags();
  const allTags = useMemo(() => tagsData?.data ?? [], [tagsData]);

  const auth = useAuth();

  const canEdit =
    auth?.user?.profile?.role === "guest" ? Case?.Privileges === 2 : true;

  const ref = useRef(null);

  // useOnClickOutside(ref, () => {
  //   if (isModified) {
  //     setShowModal(true);
  //     return;
  //   }

  //   toggle(false);
  // });

  return (
    <div ref={ref} className={styles.wrapper}>
      <div className={styles.hdr}>
        <div className={styles.img}>
          {!conflictsOnly ? <TeamIcon></TeamIcon> : <CourtIcon></CourtIcon>}
        </div>
        <div>{!conflictsOnly ? t("participants.addressList") : t("participants.similarEntities")}</div>
        {/* <CloseIcon
          onClick={() => {
            if (isModified) {
              setShowModal(true);
              return;
            }

            toggle(false);
          }}
          className={styles.closeIcon}
        /> */}
      </div>

      {!showInvitationsColumn && sourceParticipants?.length > 0 && (
        <div className={styles.hint}>
          {t("participants.conflictHint")}
        </div>
      )}

      <div className={styles.content}>
        {!conflictsOnly && (
          <SearchEntities
            removeFocus
            handleClickCreate={null}
            onChoose={(entity) => handleNewEntity(entity, false)}
            className={styles.searchEntities}
            disableBorder={true}
            label={t("common.search")}
            showCreateEntity={false}
          />
        )}
        {!conflictsOnly && (
          <div className={styles.header}>
            <div className={styles.title}>
              <AddButton text={t("participants.newEntity")} onClick={handleAdd}></AddButton>
            </div>
            <div className={styles.boxes}>
              <div className={`${styles.box} ${styles.space}`}>{t("forms.role")}</div>
              {/* <div className={`${styles.box} ${styles.space}`}>Doręczenia</div> */}
              {showInvitationsColumn && (
                <div className={`${styles.box} ${styles.space}`}>{t("participants.sharing")}</div>
              )}
              <div className={`${styles.more}`} style={{ width: "24px" }}></div>
            </div>
          </div>
        )}
        <div className={styles.list}>
          {(!participants || participants.length === 0) &&
            !addNew &&
            !isLoading &&
            !isLoadingTags &&
            (sourceParticipants?.length ?? 0) === 0 && (
              <div className={styles.empty}>
                <NotFoundScreen
                  title={t("participants.noEntities")}
                  text={t("participants.noEntitiesText")}
                ></NotFoundScreen>
              </div>
            )}
          {(isLoading || isLoadingTags) && (
            <div className={styles.empty}>
              <Spinner></Spinner>
            </div>
          )}
          {addNew && (
            <div className={classNames(styles.entityBox, styles.grow)}>
              <div className={classNames(styles.entity, styles.grow)}>
                <Avatar className={styles.avatar}></Avatar>
                <InputInline
                  value=""
                  placeholder={t("participants.enterName")}
                  onCancel={() => setAddNew(false)}
                  onChange={(v) => handleCommitAdd(v)}
                ></InputInline>
              </div>
              <div className={styles.boxes}></div>
            </div>
          )}

          {participants.map((participant) => {
            return (
              <EntityRow
                canEdit={canEdit}
                allTags={allTags}
                group={CaseParticipantGroup.Normal}
                participant={participant}
                key={participant.Id ?? participant.CourtPortalId}
                onRemove={handleRemove}
                setIsModified={setIsModified}
                onResolveConflict={handleResolveConflict}
                afterEdit={(n) => updateParticipantAfterEdit(participant, n)}
                conflictsOnly={conflictsOnly}
                showInvitationsColumn={showInvitationsColumn}
              ></EntityRow>
            );
          })}
        </div>
      </div>

      <div className={styles.btnWrapper}>
        <HollowButton
          text={t("common.cancel")}
          onClick={() => {
            if (isModified) {
              setShowModal(true);
              return;
            }

            toggle(false);
          }}
        ></HollowButton>
        <Button
          text={!conflictsOnly ? t("common.save") : t("common.confirm")}
          onClick={() => {
            save(true, true);
          }}
        />
      </div>
      {showModal && (
        <ConfirmCloseModal
          onConfirm={() => {
            save(true, true);
          }}
          onCancel={() => {
            toggle(false);
          }}
        />
      )}
    </div>
  );
};

export default CaseParticipantsModal;
